import {
  BarChartBig,
  Building2,
  Database,
  DraftingCompass,
  MessagesSquare,
  Users2,
  Wallet
} from 'lucide-react'
import { Navigate } from 'react-router-dom'

import { AppsLayout } from '../modules/apps/page'
import { AppsSandboxRequestsList } from '../modules/apps/sandbox-requests/AppsSandboxRequestsList'

import { Layout } from './Layout'
import NotFound from './NotFound'

import AuthenticationPage from '@/modules/authentication/page'
import { BlueprintManifestsList } from '@/modules/blueprints/blueprint-manifests/BlueprintManifestsList'
import { BlueprintResourceDetail } from '@/modules/blueprints/marketplace/blueprint-resources/details'
import { MarketplaceBlueprintResourcesList } from '@/modules/blueprints/marketplace/blueprint-resources/page'
import { BlueprintDetail } from '@/modules/blueprints/marketplace/blueprints/details'
import { MarketplaceBlueprintsList } from '@/modules/blueprints/marketplace/blueprints/page'
import { BlueprintsLayout } from '@/modules/blueprints/page'
import { SandboxRequestsList } from '@/modules/blueprints/sandbox-requests/SandboxRequestsList'
import GenAIFeedbackList from '@/modules/genai-feedbacks/page'
import InternalBI from '@/modules/internal-bi/page'
import { OrganizationForm } from '@/modules/organization/create-organization'
import OrganizationDetail from '@/modules/organization/details'
import OrganizationList from '@/modules/organization/page'
import { PricingTierForm } from '@/modules/pricing-tier/create-pricing-tier'
import PricingTierDetail from '@/modules/pricing-tier/details'
import PricingTierList from '@/modules/pricing-tier/page'
import { UserDetail } from '@/modules/user/details'
import UserList from '@/modules/user/page'
import VlinkSchemaOverview from '@/modules/vlink-schema/page'
import { Permissions } from '@/utils/permission-manager'

export type Route = {
  path: string
  element: JSX.Element
  icon?: JSX.Element
  label?: string
  showInMenu?: boolean
  children?: Route[]
  permission?: Permissions
}

export enum RoutePaths {
  BLUEPRINTS = '/blueprints',
  APPS = '/apps',
  BLUEPRINT_MANIFESTS = '/blueprints/blueprint-manifests',
  SANDBOX_REQUESTS = '/blueprints/sandbox-requests',
  APP_SANDBOX_REQUESTS = '/apps/sandbox-requests',
  MARKETPLACE_BLUEPRINTS = '/blueprints/marketplace-blueprints',
  MARKETPLACE_BLUEPRINT_DETAILS = '/blueprints/marketplace-blueprints/:blueprintId',
  MARKETPLACE_BLUEPRINT_CREATE = '/blueprints/marketplace-blueprints/create',
  MARKETPLACE_BLUEPRINT_RESOURCES = '/blueprints/marketplace-blueprint-resources',
  MARKETPLACE_BLUEPRINT_RESOURCE_DETAILS = '/blueprints/marketplace-blueprint-resources/:resourceId',
  MARKETPLACE_BLUEPRINT_RESOURCE_CREATE = '/blueprints/marketplace-blueprint-resources/create',
  GENAI_FEEDBACKS = '/genai-feedbacks',
  INTERNAL_BI = '/internal-bi',
  ORGANIZATIONS = '/organizations',
  ORGANIZATION_DETAIL = '/organizations/:orgId',
  ORGANIZATION_CREATE = '/organizations/create',
  PRICING_TIERS = '/pricing-tiers',
  PRICING_TIER_DETAIL = '/pricing-tiers/:pricingTierId',
  PRICING_TIER_CREATE = '/pricing-tiers/create',
  USERS = '/users',
  USER_DETAIL = '/users/:userId',
  VLINK_SCHEMA = '/vlink-schema',
  LOGIN = '/login',
  HOME = '/'
}

export const routes: Route[] = [
  {
    path: RoutePaths.HOME,
    element: <Layout />,
    children: [
      {
        path: '/',
        element: <Navigate to={RoutePaths.ORGANIZATIONS} />
      },
      {
        path: RoutePaths.ORGANIZATIONS,
        element: <OrganizationList />,
        icon: <Building2 className="text-primary" />,
        label: 'Organizations',
        showInMenu: true
      },
      {
        path: RoutePaths.ORGANIZATION_CREATE,
        element: <OrganizationForm />
      },
      {
        path: RoutePaths.ORGANIZATION_DETAIL,
        element: <OrganizationDetail />
      },
      {
        path: RoutePaths.USERS,
        element: <UserList />,
        icon: <Users2 className="text-primary" />,
        label: 'Users',
        showInMenu: true
      },
      {
        path: RoutePaths.GENAI_FEEDBACKS,
        element: <GenAIFeedbackList />,
        icon: <MessagesSquare className="text-primary" />,
        label: 'GenAI Feedbacks',
        showInMenu: true,
        permission: Permissions.VIEW_GENAI_FEEDBACKS
      },
      {
        path: RoutePaths.USER_DETAIL,
        element: <UserDetail />
      },
      {
        path: RoutePaths.PRICING_TIERS,
        element: <PricingTierList />,
        label: 'Pricing Tiers',
        icon: <Wallet className="text-primary" />,
        showInMenu: true
      },
      {
        path: RoutePaths.PRICING_TIER_CREATE,
        element: <PricingTierForm />
      },
      {
        path: RoutePaths.PRICING_TIER_DETAIL,
        element: <PricingTierDetail />
      },
      {
        path: RoutePaths.INTERNAL_BI,
        element: <InternalBI />,
        label: 'Internal BI',
        icon: <BarChartBig className="text-primary" />,
        showInMenu: true
      },
      {
        path: RoutePaths.VLINK_SCHEMA,
        element: <VlinkSchemaOverview />,
        label: 'Vlink Database Schema',
        icon: <Database className="text-primary" />,
        showInMenu: true,
        permission: Permissions.VLINK_ADMIN
      },
      {
        path: RoutePaths.BLUEPRINTS,
        element: <BlueprintsLayout />,
        label: 'Blueprints',
        icon: <DraftingCompass className="text-primary" />,
        showInMenu: true,
        children: [
          {
            path: RoutePaths.BLUEPRINTS,
            element: <Navigate to={RoutePaths.MARKETPLACE_BLUEPRINTS} />
          },
          {
            path: RoutePaths.MARKETPLACE_BLUEPRINTS,
            element: <MarketplaceBlueprintsList />,
            showInMenu: true,
            label: 'Marketplace Blueprints',
            icon: <DraftingCompass className="text-primary" />
          },
          {
            path: RoutePaths.MARKETPLACE_BLUEPRINT_RESOURCES,
            element: <MarketplaceBlueprintResourcesList />,
            showInMenu: true,
            label: 'Marketplace Blueprint Resources',
            icon: <DraftingCompass className="text-primary" />
          },
          {
            path: RoutePaths.BLUEPRINT_MANIFESTS,
            element: <BlueprintManifestsList />,
            showInMenu: true,
            label: 'Installed Blueprint Manifests',
            icon: <DraftingCompass className="text-primary" />
          },
          {
            path: RoutePaths.SANDBOX_REQUESTS,
            element: <SandboxRequestsList />,
            showInMenu: true,
            label: 'Sandbox Requests',
            icon: <DraftingCompass className="text-primary" />
          },
          {
            path: RoutePaths.MARKETPLACE_BLUEPRINT_CREATE,
            element: <BlueprintDetail />
          },
          {
            path: RoutePaths.MARKETPLACE_BLUEPRINT_DETAILS,
            element: <BlueprintDetail />
          },
          {
            path: RoutePaths.MARKETPLACE_BLUEPRINT_RESOURCE_CREATE,
            element: <BlueprintResourceDetail />
          },
          {
            path: RoutePaths.MARKETPLACE_BLUEPRINT_RESOURCE_DETAILS,
            element: <BlueprintResourceDetail />
          }
        ]
      },
      {
        path: RoutePaths.APPS,
        element: <AppsLayout />,
        label: 'Apps',
        icon: <DraftingCompass className="text-primary" />,
        showInMenu: true,
        children: [
          {
            path: RoutePaths.APPS,
            element: <Navigate to={RoutePaths.APP_SANDBOX_REQUESTS} />
          },
          {
            path: RoutePaths.APP_SANDBOX_REQUESTS,
            element: <AppsSandboxRequestsList />,
            showInMenu: false,
            label: 'Sandbox Requests',
            icon: <DraftingCompass className="text-primary" />
          }
        ]
      },
      {
        path: '*',
        element: <NotFound />
      }
    ]
  },
  {
    path: RoutePaths.LOGIN,
    element: <AuthenticationPage />
  }
]

export const getMenuItems = () => {
  return (
    routes
      .find((x) => x.path === RoutePaths.HOME)
      ?.children?.filter((x) => x.showInMenu) ?? []
  )
}

export const getAllRoutes = () => {
  const allRoutes: Route[] = []

  routes.forEach((route) => {
    if (route.children) {
      route.children.forEach((child) => {
        allRoutes.push(child)
      })
    } else {
      allRoutes.push(route)
    }
  })

  return allRoutes
}
